import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { housesApi } from '../features/houses/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { hasCensus, hasZone } from '../features/zone/access.ts';

export function Dashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!user && (user.isOrgAdmin || user.role === 'ADMIN');

  const { data: houses } = useQuery({
    queryKey: ['houses', 'count'],
    queryFn: () => housesApi.list({ page: 1, pageSize: 1 }),
    enabled: !!user && user.role !== 'SYS_ADMIN' && !!user.orgId && hasCensus(user),
  });

  if (user?.role === 'SYS_ADMIN') return <Navigate to="/admin/orgs" />;
  // Zone-only users have no census dashboard.
  if (user && user.orgId && !hasCensus(user) && hasZone(user)) return <Navigate to="/zone" />;

  return (
    <div className="page space-y-8">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl tracking-tight">{dv.dashboard.welcome}، {user?.name}</h1>
        {user && (
          <p className="text-sm text-muted-foreground">{user.designationEn ?? (user.role ? dv.roles[user.role] : '')}</p>
        )}
      </header>

      {/* Quick links */}
      <section className="space-y-3">
        <h2 className="section-heading">
          <span className="font-serif text-lg font-bold">{dv.dashboard.quickLinks}</span>
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Link to="/houses" className="card-paper flex items-center gap-3 p-4 transition-colors hover:bg-muted/50">
            <Icon name="home" size={20} className="shrink-0 text-muted-foreground" />
            <span className="flex-1 font-medium">{dv.dashboard.housesLink}</span>
            {houses && (
              <span className="tabular mono-meta text-muted-foreground" dir="ltr">{houses.total}</span>
            )}
            <Icon name="arrow_back" size={16} className="shrink-0 text-muted-foreground" />
          </Link>
          {isAdmin && (
            <Link to="/dhaftharu" className="card-paper flex items-center gap-3 p-4 transition-colors hover:bg-muted/50">
              <Icon name="menu_book" size={20} className="shrink-0 text-muted-foreground" />
              <span className="flex-1 font-medium">{dv.dashboard.dhaftharuLink}</span>
              <Icon name="arrow_back" size={16} className="shrink-0 text-muted-foreground" />
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}
